import moment from 'moment'
import { AxiosError } from 'axios'
import { Expose, Transform, Type } from 'class-transformer'
import { Truck } from 'react-feather'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'
import { handleErrorMessage } from 'src/components/Notification'

import { Address } from './Address'
import { Base } from './Base'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { Facility } from './Facility'
import { FreightOrder } from './FreightOrder'
import { Place } from './Place'
import { Truckload } from './Truckload'

enum FreightShipmentStatus {
  pending = 'Pending',
  shipped = 'Shipped',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

export enum FreightShipmentStatusEnum {
  pending = 'pending',
  shipped = 'shipped',
  delivered = 'delivered',
  canceled = 'canceled',
}

export class FreightShipment extends Base {
  static type = 'freightshipment'
  static typeDisplay = 'Shipment'
  static icon = Truck

  static Status = FreightShipmentStatus

  type = FreightShipment.type
  typeDisplay = FreightShipment.typeDisplay
  icon = FreightShipment.icon

  @Expose({ name: 'xid' })
  guid: string = ''

  name: string = ''

  @Expose({ name: 'tracking_number' })
  trackingNumber?: string

  bol?: string

  @Expose({ name: 'shipment_id' })
  shipmentId?: string

  @Expose({ name: 'pro_number' })
  proNumber?: string

  @Expose({ name: 'internal_notes' })
  internalNotes?: string

  @Type(() => Client)
  client: Client = new Client()

  @Type(() => Facility)
  facility?: Facility

  @Type(() => Place)
  place?: Place

  @Type(() => FreightOrder)
  order: FreightOrder = new FreightOrder()

  @Type(() => Truckload)
  truckload?: Truckload

  @Type(() => Address)
  address?: Address

  @Type(() => Carrier)
  carrier?: Carrier

  status?: string

  @Expose({ name: 'shipped_on' })
  @Transform((value) => (value ? moment(value) : value))
  shippedOn?: moment.Moment

  @Expose({ name: 'delivered_on' })
  @Transform((value) => (value ? moment(value) : value))
  deliveredOn?: moment.Moment

  @Expose({ name: 'canceled_on' })
  @Transform((value) => (value ? moment(value) : value))
  canceledOn?: moment.Moment

  get objPath() {
    const items = []
    if (this.place) {
      items.push(...this.place.objPath)
    }
    items.push(this)
    return items
  }

  get workflowStatus() {
    if (this.status) {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
    return ''
  }

  get workflow(): WorkflowSequence {
    if (this.status === FreightShipmentStatusEnum.canceled) {
      return [
        [FreightShipmentStatus.pending, this.createdOn],
        [FreightShipmentStatus.canceled, this.canceledOn],
      ]
    }
    return [
      [FreightShipmentStatus.pending, this.createdOn],
      [FreightShipmentStatus.shipped, this.shippedOn],
      [FreightShipmentStatus.delivered, this.deliveredOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [FreightShipmentStatus.pending]: {
        forward: [
          {
            label: 'Ship',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'canceled' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightShipmentStatus.shipped]: {
        forward: [
          {
            label: 'Deliver',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'delivered' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Unship',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'pending' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightShipmentStatus.delivered]: {
        backward: [
          {
            label: 'Undeliver',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'shipped' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [FreightShipmentStatus.canceled]: {
        backward: [
          {
            label: 'Uncancel',
            onClick: ({ complete }) => {
              skuit.sc.freight.shipments
                .patch(this.guid, { status: 'pending' })
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  handleErrorMessage(error, 'updat', 'Freight Shipment')
                )
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}
